import { makeAutoObservable } from 'mobx';
import { ExerciseTemplate, BundledExercise } from 'exercises/types';
import API from 'utils/API';
import exerciseData from 'exercises/exerciseData.json';
import { isLanguage, localeSelect } from 'hevy-shared';
import { stores } from 'state/stores';

const CUSTOM_EXERCISES_LOCAL_STORAGE_KEY = 'CUSTOM_EXERCISES_LOCAL_STORAGE_KEY';

export class ExerciseTemplates {
  private _customExercises: ExerciseTemplate[] = [];
  private _bundledTemplates: BundledExercise[] = [];

  constructor() {
    makeAutoObservable(this);
    this._bundledTemplates = Object.values(exerciseData) as BundledExercise[];
  }

  get exercises(): ExerciseTemplate[] {
    const allExercises = [...this._customExercises, ...this._bundledTemplates];
    return allExercises
      .filter(e => e.is_archived === false)
      .sort(function (a, b) {
        const textA = a.title.toUpperCase();
        const textB = b.title.toUpperCase();
        return textA < textB ? -1 : textA > textB ? 1 : 0;
      });
  }

  get bundledTemplates(): BundledExercise[] {
    return this._bundledTemplates;
  }

  get hundredPercentBodyweightExercises(): BundledExercise[] {
    return this._bundledTemplates.filter(e => e.hundred_percent_bodyweight_exercise);
  }

  getExercise(templateId: string): ExerciseTemplate | undefined {
    const maybeBundledExercise = this._bundledTemplates.find(e => e.id === templateId);

    return maybeBundledExercise
      ? maybeBundledExercise
      : this._customExercises.find(e => e.id === templateId);
  }

  getExerciseTitle = (templateId: string): string | undefined => {
    const template = this.getExercise(templateId);

    if (!template) {
      return undefined;
    }

    const language = stores.userPreferences.language;

    if (!template.is_custom) {
      return localeSelect(language, {
        en: template.title,
        ar: template.ar_title,
        ca: template.ca_title,
        es: template.es_title,
        de: template.de_title,
        fr: template.fr_title,
        hi: template.hi_title,
        it: template.it_title,
        pl: template.pl_title,
        pt_BR: template.pt_br_title,
        pt_PT: template.pt_title,
        tr: template.tr_title,
        zh_CN: template.zh_cn_title,
        zh_TW: template.zh_tw_title,
        ru: template.ru_title,
        ja: template.ja_title,
        ko: template.ko_title,
      });
    }

    return template.title;
  };

  getExerciseInstructions(templateId: string): string | undefined {
    const template = this.getExercise(templateId);

    if (!template) {
      return undefined;
    }

    if (template?.is_custom) return undefined;

    const language = stores.userPreferences.language;

    const enInstructions = template.localised_instructions?.['en']?.trim();

    if (isLanguage(language)) {
      const instructions = template.localised_instructions?.[language]?.trim() || enInstructions;

      return instructions;
    }

    return enInstructions;
  }

  hydrate = () => {
    const customExercisesJson = window.localStorage.getItem(CUSTOM_EXERCISES_LOCAL_STORAGE_KEY);
    if (customExercisesJson) {
      this._customExercises = JSON.parse(customExercisesJson);

      window.localStorage.setItem(
        CUSTOM_EXERCISES_LOCAL_STORAGE_KEY,
        JSON.stringify(this._customExercises),
      );
    }
  };

  clearData = () => {
    this._customExercises = [];
    window.localStorage.removeItem(CUSTOM_EXERCISES_LOCAL_STORAGE_KEY);
  };

  fetch = async () => {
    const result = await API.getCustomExerciseTemplates();
    this._customExercises = result.data;
    window.localStorage.setItem(
      CUSTOM_EXERCISES_LOCAL_STORAGE_KEY,
      JSON.stringify(this._customExercises),
    );
  };
}
